import React, { useEffect, useMemo, useState } from 'react';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import { styles } from './styles';

export default function EditLinkConfigModal({
  open = false,
  onClose,
  onSave,
  checklistTitle = '',
  responsesCount = 0,
  initialAnonymous = false,
  initialDays = 0,
  initialClosed = false,
  isLinkClosed = false,
}) {
  const [anonymous, setAnonymous] = useState(initialAnonymous);
  const [days, setDays] = useState(initialDays);
  const [closed, setClosed] = useState(initialClosed);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isReadOnly = saving || !!isLinkClosed;
  const isAnonymousReadOnly = isReadOnly || !!initialAnonymous;
  const closedValue = !!isLinkClosed ? true : !!closed;

  useEffect(() => {
    if (!open) return;
    setAnonymous(!!initialAnonymous);

    const parsedDays = Number.isFinite(initialDays) ? Number(initialDays) : 0;
    setDays(parsedDays);
    setClosed(!!isLinkClosed ? true : !!initialClosed);
    setSaving(false);
    setError('');
  }, [open, initialAnonymous, initialDays, initialClosed, isLinkClosed]);

  useEffect(() => {
    function onEsc(e) {
      if (e.key === 'Escape') {
        if (!saving && typeof onClose === 'function') onClose();
      }
    }
    if (open) document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open, onClose, saving]);

  const daysLabel = useMemo(() => {
    if (!days || days <= 0) return 'Sem expiração';
    return `${days} ${days === 1 ? 'dia' : 'dias'}`;
  }, [days]);

  if (!open) return null;

  async function handleSave() {
    if (isReadOnly || typeof onSave !== 'function') {
      if (typeof onClose === 'function') onClose();
      return;
    }

    try {
      setSaving(true);
      setError('');

      await onSave({
        anonymous: !!anonymous,
        days: Number(days) || 0,
        closed: !!isLinkClosed ? true : !!closed,
      });

      if (typeof onClose === 'function') onClose();
    } catch (e) {
      setError('Não foi possível salvar as alterações. Tente novamente.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      style={styles.backdrop}
      onMouseDown={() => {
        if (saving) return;
        if (typeof onClose === 'function') onClose();
      }}
    >
      <div
        style={styles.modal}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div style={styles.header}>
          <div style={styles.headerTitle}>Editar Configurações do Link</div>

          <button
            type="button"
            onClick={() => !saving && typeof onClose === 'function' && onClose()}
            style={styles.closeBtn}
            aria-label="Fechar"
            title="Fechar"
            disabled={saving}
          >
            <HighlightOffOutlinedIcon style={{ fontSize: 20, color: '#6B7280' }} />
          </button>
        </div>

        <div style={styles.topCard}>
          <div style={styles.topCardTitle}>{checklistTitle}</div>
          <div style={styles.topCardSub}>
            {responsesCount} {responsesCount === 1 ? 'resposta recebida' : 'respostas recebidas'}
          </div>
        </div>

        <div style={styles.divider} />

        <div style={styles.row}>
          <div style={styles.rowLeft}>
            <div style={styles.rowIcon}>
              <PersonOffOutlinedIcon style={{ fontSize: 20, color: '#6B7280' }} />
            </div>
            <div>
              <div style={styles.rowTitle}>Resposta Anônima</div>
              <div style={styles.rowSub}>Não solicitar identificação do respondente</div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              if (isAnonymousReadOnly) return;
              setAnonymous((p) => !p);
            }}
            style={{
              ...styles.switchWrap,
              cursor: isAnonymousReadOnly ? 'not-allowed' : 'pointer',
              opacity: isAnonymousReadOnly ? 0.65 : 1,
              pointerEvents: isAnonymousReadOnly ? 'none' : 'auto',
            }}
            aria-label="Alternar resposta anônima"
            disabled={isAnonymousReadOnly}
          >
            <span style={styles.switchTrack(!!anonymous)}>
              <span style={styles.switchThumb(!!anonymous)} />
            </span>
          </button>
        </div>

        <div style={styles.row}>
          <div style={styles.rowLeft}>
            <div style={styles.rowIcon}>
              <BlockOutlinedIcon style={{ fontSize: 20, color: '#6B7280' }} />
            </div>
            <div>
              <div style={styles.rowTitle}>Fechar Link</div>
              <div style={styles.rowSub}>
                {!!isLinkClosed
                  ? 'Link já está encerrado e não pode ser reaberto'
                  : 'Impede novas respostas via link'}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              if (isReadOnly) return;
              setClosed((p) => !p);
            }}
            style={{
              ...styles.switchWrap,
              cursor: isReadOnly ? 'not-allowed' : 'pointer',
              opacity: isReadOnly ? 0.65 : 1,
              pointerEvents: isReadOnly ? 'none' : 'auto',
            }}
            aria-label="Alternar link fechado"
            disabled={isReadOnly}
          >
            <span style={styles.switchTrack(closedValue)}>
              <span style={styles.switchThumb(closedValue)} />
            </span>
          </button>
        </div>

        <div style={{ ...styles.row, alignItems: 'flex-start' }}>
          <div style={styles.rowLeft}>
            <div style={styles.rowIcon}>
              <AccessTimeOutlinedIcon style={{ fontSize: 20, color: '#6B7280' }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={styles.rowTitle}>Expiração do Link</div>
              <div style={styles.rowSub}>O link expira após {daysLabel}</div>
            </div>
          </div>

          <div style={styles.daysPill}>{daysLabel}</div>
        </div>

        <div style={{ ...styles.sliderWrap, opacity: isReadOnly ? 0.65 : 1 }}>
          <input
            type="range"
            min={0}
            max={31}
            value={Number(days) || 0}
            onChange={(e) => {
              if (isReadOnly) return;
              setDays(Number(e.target.value));
            }}
            style={styles.slider}
            disabled={isReadOnly}
          />
          <div style={styles.sliderLabels}>
            <span style={styles.sliderLabel}>0 (sem expiração)</span>
            <span style={styles.sliderLabel}>31 dias</span>
          </div>
        </div>

        {isLinkClosed ? (
          <div
            style={{
              ...styles.warnBox,
              backgroundColor: '#FEF2F2',
              borderColor: '#FCA5A5',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <HighlightOffOutlinedIcon
              style={{
                fontSize: 18,
                color: '#DC2626',
                flexShrink: 0,
              }}
            />
            <div
              style={{
                ...styles.warnText,
                color: '#DC2626',
                fontWeight: 600,
                lineHeight: '18px',
              }}
            >
              Este link está encerrado e não pode ser editado.
            </div>
          </div>
        ) : (
          <div style={styles.warnBox}>
            <WarningAmberOutlinedIcon style={{ fontSize: 18, color: '#F59E0B' }} />
            <div style={styles.warnText}>
              Alterações nas configurações não afetam respostas já recebidas.
            </div>
          </div>
        )}

        {!!error && (
          <div style={{ marginTop: 10, color: '#B91C1C', fontWeight: 700, fontSize: 13 }}>
            {error}
          </div>
        )}

        <div style={styles.footer}>
          <button
            type="button"
            style={{ ...styles.cancelBtn, opacity: saving ? 0.6 : 1 }}
            onClick={() => !saving && typeof onClose === 'function' && onClose()}
            disabled={saving}
          >
            Cancelar
          </button>

          <button
            type="button"
            style={{
              ...styles.saveBtn,
              opacity: isReadOnly ? 0.55 : 1,
              cursor: isReadOnly ? 'not-allowed' : 'pointer',
            }}
            onClick={handleSave}
            disabled={isReadOnly}
            title={isLinkClosed ? 'Link encerrado não permite edição' : 'Salvar alterações'}
          >
            <SaveOutlinedIcon style={{ fontSize: 18, color: '#FFFFFF' }} />
            {saving ? 'Salvando...' : 'Salvar Alterações'}
          </button>
        </div>
      </div>
    </div>
  );
}
